// Pure draft-mutation actions for the program editor.
//
// Companion to programEdit.ts (the matrix model + pivots). These are the
// immutable `(draft, …args) => Draft` operations the editor UI binds to
// its buttons/inputs. Lifting them out of the React component lets both
// the legacy web editor and the shared @jackedlogs/ui editor share one
// tested core, and makes the non-trivial ones (seed-from-last, kind
// re-seed, move bounds, group derivation) unit-testable without a UI.
// Each returns the input draft unchanged when the target index is out of
// range — callers never have to guard. Keep free of React / UI imports.

import {
  authoredTextIn,
  promoteToLocalized,
  setAuthoredLocale,
  type AuthoredText as AuthoredTextT,
} from "./authoredText.js";
import type { Locale } from "./locale.js";
import {
  blankCell,
  blankSetRow,
  blankSlot,
  seedForIntensityKind,
  VALUELESS_KINDS,
  type Draft,
  type IntensityKind,
  type SessionDraft,
  type SetRowDraft,
  type SlotDraft,
  type SlotGroupDraft,
  type CellDraft,
} from "./programEdit.js";
import { hasActiveWeek } from "./programBlocks.js";

export const updateSession = (
  draft: Draft,
  idx: number,
  patch: Partial<SessionDraft>,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
});

// PE-11 — structural edits the editor previously only allowed at creation
// (NewProgramSetup). A program's day count and cycle length were fixed once
// composing; these grow/shrink them in edit-in-place. Schema bounds: sessions
// 1..7, cycleWeeks 1..12. New sessions/slots carry id "" so the server mints
// ids on save (same convention as blankSlot), and a fresh day starts empty —
// the save guard ("every day needs an exercise") nudges the user to fill it.

// Schema bounds (schemas/program.ts) exported so the editor UI clamps against
// the same numbers instead of re-typing 7 / 1 / 12 at every stepper + cap.
export const MAX_SESSIONS = 7;
export const MIN_CYCLE_WEEKS = 1;
export const MAX_CYCLE_WEEKS = 12;

export const addSession = (draft: Draft, name: string): Draft =>
  draft.sessions.length >= MAX_SESSIONS
    ? draft
    : {
        ...draft,
        sessions: [...draft.sessions, { id: "", name, notes: "", slots: [] }],
        // Adding a training day implies at least that many sessions/week; keep
        // the higher of the two (sessionsPerWeek stays independent otherwise).
        sessionsPerWeek: Math.min(
          MAX_SESSIONS,
          Math.max(draft.sessionsPerWeek, draft.sessions.length + 1),
        ),
      };

export const removeSession = (draft: Draft, idx: number): Draft => {
  if (draft.sessions.length <= 1 || idx < 0 || idx >= draft.sessions.length) {
    return draft;
  }
  const nextLen = draft.sessions.length - 1;
  return {
    ...draft,
    sessions: draft.sessions.filter((_, i) => i !== idx),
    sessionsPerWeek: Math.min(draft.sessionsPerWeek, nextLen),
  };
};

// Grow or shrink the cycle. Growing copies the LAST week's cells/notes/inactive
// flag into each new week (Antenore's pick — a new week extends the ramp, then
// you tweak it), so every per-week array (cells, notesPerCell, inactiveWeeks)
// stays exactly cycleWeeks long. Shrinking truncates from the end — and a slot
// active ONLY in the dropped tail (e.g. an accessory run W4–W6, cycle cut to 3)
// would be left with zero active weeks: a phantom that derives to a verbatim
// card and blocks Save ("slot inactive on every week"). We sweep those away —
// dropping the tail weeks drops their exclusive sets, exactly what the shrink
// confirm promises.
export const setCycleWeeks = (draft: Draft, weeks: number): Draft => {
  const next = Math.max(
    MIN_CYCLE_WEEKS,
    Math.min(MAX_CYCLE_WEEKS, Math.floor(weeks)),
  );
  if (next === draft.cycleWeeks) return draft;
  const resize = <T,>(arr: T[], fill: (last: T | undefined) => T): T[] =>
    next <= arr.length
      ? arr.slice(0, next)
      : [
          ...arr,
          ...Array.from({ length: next - arr.length }, () =>
            fill(arr[arr.length - 1]),
          ),
        ];
  return {
    ...draft,
    cycleWeeks: next,
    sessions: draft.sessions.map((s) => ({
      ...s,
      slots: s.slots
        .map((sl) => ({
          ...sl,
          inactiveWeeks: resize(sl.inactiveWeeks, (last) => last ?? false),
          setRows: sl.setRows.map((r) => ({
            ...r,
            cells: resize(r.cells, (last) => ({ ...(last ?? blankCell()) })),
            notesPerCell: resize(r.notesPerCell, (last) => last ?? ""),
            // Stays aligned with notesPerCell when present: a week added
            // to the cycle has no stored translation behind it yet.
            ...(r.notesRawPerCell
              ? { notesRawPerCell: resize(r.notesRawPerCell, () => undefined) }
              : {}),
          })),
        }))
        .filter(hasActiveWeek),
    })),
  };
};

export const updateSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  patch: Partial<SlotDraft>,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => {
    if (i !== sIdx) return s;
    return {
      ...s,
      slots: s.slots.map((sl, j) =>
        j === slotIdx ? { ...sl, ...patch } : sl,
      ),
    };
  }),
});

export const addSlot = (draft: Draft, sIdx: number): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) =>
    i === sIdx
      ? { ...s, slots: [...s.slots, blankSlot(draft.cycleWeeks)] }
      : s,
  ),
});

export const removeSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) =>
    i === sIdx
      ? { ...s, slots: s.slots.filter((_, j) => j !== slotIdx) }
      : s,
  ),
});

export const moveSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  delta: -1 | 1,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => {
    if (i !== sIdx) return s;
    const target = slotIdx + delta;
    if (target < 0 || target >= s.slots.length) return s;
    const next = [...s.slots];
    const tmp = next[slotIdx]!;
    next[slotIdx] = next[target]!;
    next[target] = tmp;
    return { ...s, slots: next };
  }),
});

export const setSlotGroup = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  group: SlotGroupDraft | null,
): Draft => updateSlot(draft, sIdx, slotIdx, { group });

// Internal: the shared row patcher updateCell and setRowKind both go
// through. Not exported — every editor reaches rows via those two, or via
// the composer's own same-named useCallback, and an `export *` barrel makes
// an internal helper look like public API to anyone grepping for it.
const updateRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  patch: Partial<SetRowDraft>,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot) return draft;
  return updateSlot(draft, sIdx, slotIdx, {
    setRows: slot.setRows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r)),
  });
};

export const updateCell = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  weekIdx: number,
  patch: Partial<CellDraft>,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  const row = slot?.setRows[rowIdx];
  if (!slot || !row) return draft;
  return updateRow(draft, sIdx, slotIdx, rowIdx, {
    cells: row.cells.map((c, i) => (i === weekIdx ? { ...c, ...patch } : c)),
  });
};

export const addSetRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot) return draft;
  // Seed the new row from the last existing one so adding a back-off set
  // after a top-set inherits a sensible starting point.
  const last = slot.setRows[slot.setRows.length - 1];
  const seed: SetRowDraft = last
    ? {
        intensityKind: last.intensityKind,
        // ⚠️ workKind is on the ROW while the seconds are on the CELL, and
        // the cell spread below already carries them. Omitting it here left
        // the new set holding a duration inside a row that no longer
        // claimed to be timed — text on screen that the build path would
        // never emit. This whitelist is exactly where that goes unnoticed.
        ...(last.workKind ? { workKind: last.workKind } : {}),
        cells: last.cells.map((c) => ({ ...c })),
        // A NEW set inherits no note, so it inherits no stored value
        // either — grafting the previous set's translations onto it
        // would attribute words to a set nobody wrote them for.
        notesPerCell: last.notesPerCell.map(() => ""),
      }
    : blankSetRow(draft.cycleWeeks);
  return updateSlot(draft, sIdx, slotIdx, {
    setRows: [...slot.setRows, seed],
  });
};

export const removeSetRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot || slot.setRows.length <= 1) return draft;
  return updateSlot(draft, sIdx, slotIdx, {
    setRows: slot.setRows.filter((_, i) => i !== rowIdx),
  });
};

export const setRowKind = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  kind: IntensityKind,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  const row = slot?.setRows[rowIdx];
  if (!slot || !row) return draft;
  // Value-less kinds (none, history) blank the value column so leftover
  // digits don't read as something the kind ignores. When switching
  // between value-bearing kinds, keep the existing values — usually the
  // user is just toggling between %MAX and absolute on the same numbers.
  const valueless = VALUELESS_KINDS.has(kind);
  const seedValue = seedForIntensityKind(kind);
  return updateRow(draft, sIdx, slotIdx, rowIdx, {
    intensityKind: kind,
    cells: row.cells.map((c) => ({
      ...c,
      intensityValue: valueless ? "" : c.intensityValue || seedValue,
    })),
  });
};

// Assignee chip-row actions. addAssigneeEmail trims + lowercases + dedupes
// + loose-validates; an invalid or duplicate value returns the draft
// unchanged (the UI clears its input field regardless). Same rules as the
// legacy editor's addEmailFromDraft.
export const addAssigneeEmail = (draft: Draft, raw: string): Draft => {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return draft;
  if (draft.assigneeEmails.includes(trimmed)) return draft;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return draft;
  return { ...draft, assigneeEmails: [...draft.assigneeEmails, trimmed] };
};

export const removeAssigneeEmail = (draft: Draft, email: string): Draft => ({
  ...draft,
  assigneeEmails: draft.assigneeEmails.filter((e) => e !== email),
});

export const removeAssignee = (draft: Draft, sub: string): Draft => ({
  ...draft,
  assignedTo: draft.assignedTo.filter((s) => s !== sub),
});

// ⧉ Clona settimana (Antenore 2026-06-11, the 5/3/1 flow: fill W1
// vertically across the days, clone it onto W2..Wn, then ramp each week
// with the per-week edit). A pure column copy on the Draft: every slot
// active in `fromWeek` gets its cells/notes copied onto each target
// week. Refuses — never half-clones — when ANY slot already has content
// in a target week (the predictable rule; remove or edit that first).
export type CloneWeekResult =
  | { ok: true; draft: Draft }
  | { ok: false; reason: "empty" }
  | {
      ok: false;
      reason: "conflict";
      conflict: { exerciseId: string; dayIdx: number; week: number };
    };

export const cloneWeekInDraft = (
  draft: Draft,
  fromWeek: number,
  toWeeks: number[],
): CloneWeekResult => {
  const targets = [...new Set(toWeeks)]
    .filter((w) => w !== fromWeek && w >= 0 && w < draft.cycleWeeks)
    .sort((a, b) => a - b);
  const anySource = draft.sessions.some((s) =>
    s.slots.some((slot) => !slot.inactiveWeeks[fromWeek]),
  );
  if (targets.length === 0 || !anySource) return { ok: false, reason: "empty" };

  for (const [dayIdx, session] of draft.sessions.entries()) {
    for (const slot of session.slots) {
      for (const w of targets) {
        if (!slot.inactiveWeeks[w]) {
          return {
            ok: false,
            reason: "conflict",
            conflict: { exerciseId: slot.exerciseId, dayIdx, week: w },
          };
        }
      }
    }
  }

  return {
    ok: true,
    draft: {
      ...draft,
      sessions: draft.sessions.map((session) => ({
        ...session,
        slots: session.slots.map((slot) => {
          if (slot.inactiveWeeks[fromWeek]) return slot;
          const inactiveWeeks = [...slot.inactiveWeeks];
          for (const w of targets) inactiveWeeks[w] = false;
          return {
            ...slot,
            inactiveWeeks,
            setRows: slot.setRows.map((row) => {
              const cells = [...row.cells];
              const notesPerCell = [...row.notesPerCell];
              for (const w of targets) {
                cells[w] = { ...row.cells[fromWeek]! };
                notesPerCell[w] = row.notesPerCell[fromWeek] ?? "";
              }
              return { ...row, cells, notesPerCell };
            }),
          };
        }),
      })),
    },
  };
};

// PE-21 slice B — switch the language the editor is WRITING.
//
// Antenore's words: *«un selettore di lingua nell'editor programmi, e la
// UI salvi il contenuto nella cache spostandosi da uno all'altro»*. The
// cache is the draft itself: every visible string is first grafted back
// into the language being left, then the strings for the language being
// entered are read out of storage.
//
// ⛔ The read is `authoredTextIn`, NOT `pickAuthoredText`: a language
// with nothing in it must come up EMPTY. Falling back would show the
// coach the Italian under a French flag, they would type over it, and the
// Italian would be gone while the French was never written.
//
// ⛔ Promotion to a per-language object happens HERE and nowhere else.
// Slice A promises that a program nobody translates keeps its naked
// strings for ever, saves included. Asking for a second language is the
// moment that promise has done its job — there is nowhere else to put
// the first one.
//
// Returns the draft untouched when there is no language to leave (a Plan
// draft, `editLocale: null`) or when the target is the current one.
export const changeEditLocale = (draft: Draft, next: Locale): Draft => {
  const from = draft.editLocale;
  if (!from || from === next) return draft;

  // The string on screen belongs to the language being left. Carry it
  // into storage, then read the target language back out.
  const carry = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): AuthoredTextT | undefined =>
    setAuthoredLocale(promoteToLocalized(raw, from) ?? {}, from, text);

  const swap = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): { raw: AuthoredTextT | undefined; text: string } => {
    const carried = carry(raw, text);
    return { raw: carried, text: authoredTextIn(carried, next) };
  };

  const name = swap(draft.nameRaw, draft.name);
  const description = swap(draft.descriptionRaw, draft.description);

  return {
    ...draft,
    editLocale: next,
    name: name.text,
    nameRaw: name.raw,
    description: description.text,
    descriptionRaw: description.raw,
    sessions: draft.sessions.map((s) => {
      const sName = swap(s.nameRaw, s.name);
      const sNotes = swap(s.notesRaw, s.notes);
      return {
        ...s,
        name: sName.text,
        nameRaw: sName.raw,
        notes: sNotes.text,
        notesRaw: sNotes.raw,
        slots: s.slots.map((sl) => {
          const instr = swap(sl.instructionsRaw, sl.instructions);
          return {
            ...sl,
            instructions: instr.text,
            instructionsRaw: instr.raw,
            setRows: sl.setRows.map((r) => {
              const swapped = r.notesPerCell.map((text, w) =>
                swap(r.notesRawPerCell?.[w], text),
              );
              return {
                ...r,
                notesPerCell: swapped.map((c) => c.text),
                notesRawPerCell: swapped.map((c) => c.raw),
              };
            }),
          };
        }),
      };
    }),
  };
};
